import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listVisitors, mailEventsURL, type SiteVisitor } from "../api/mailTracking";
import { ortamCoz, dilCoz, kaynakCoz } from "../lib/ortam";
import Icon from "../components/icons";

function saat(s: string): string {
  try {
    return new Date(s).toLocaleTimeString("tr-TR", {
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch {
    return s;
  }
}

function gecenSure(s: string): string {
  const sn = Math.floor(Math.max(0, Date.now() - new Date(s).getTime()) / 1000);
  if (sn < 60) return `${sn} sn önce`;
  const dk = Math.floor(sn / 60);
  if (dk < 60) return `${dk} dk önce`;
  const sa = Math.floor(dk / 60);
  if (sa < 24) return `${sa} sa önce`;
  return `${Math.floor(sa / 24)} gün önce`;
}

const ARALIKLAR = [
  { dk: 15, etiket: "15 dakika" },
  { dk: 60, etiket: "1 saat" },
  { dk: 60 * 24, etiket: "24 saat" },
  { dk: 60 * 24 * 7, etiket: "7 gün" },
];

// Sitede gezinenler — mailden gelmeyen ziyaretçiler dahil.
//
// Ölçüm nginx `mirror` ile sunucu tarafında yapılır: hem çerezsiz ziyaretçiyi
// yakalar hem de reklam engelleyicilerden etkilenmez. Mailden gelen ziyaretçi
// ayrıca alıcı adıyla eşleşir.
export default function Ziyaretciler() {
  const navigate = useNavigate();
  const [liste, setListe] = useState<SiteVisitor[]>([]);
  const [dakika, setDakika] = useState(60);
  const [arama, setArama] = useState("");
  const [yukleniyor, setYukleniyor] = useState(true);
  const [canli, setCanli] = useState(false);

  const yukle = useCallback(async () => {
    try {
      setListe(await listVisitors(dakika));
    } catch {
      /* sessiz: liste boş kalır, panelin geri kalanı çalışmaya devam eder */
    } finally {
      setYukleniyor(false);
    }
  }, [dakika]);

  useEffect(() => { void yukle(); }, [yukle]);

  // Canlı: her sayfa görüntülemesinde tazelenir. Ayrıca 30 sn'lik yedek
  // yoklama — SSE reklam engelleyiciye takılırsa liste yine de güncellensin.
  useEffect(() => {
    const es = new EventSource(mailEventsURL());
    let z: number | undefined;
    const tazele = () => {
      window.clearTimeout(z);
      z = window.setTimeout(() => void yukle(), 800);
    };
    es.onopen = () => setCanli(true);
    es.onerror = () => setCanli(false);
    for (const t of ["open", "click", "pageview"]) es.addEventListener(t, tazele);
    const yoklama = window.setInterval(() => void yukle(), 30000);
    return () => {
      window.clearTimeout(z);
      window.clearInterval(yoklama);
      es.close();
    };
  }, [yukle]);

  const q = arama.trim().toLowerCase();
  const gorunen = q
    ? liste.filter((v) =>
        (v.email ?? "").toLowerCase().includes(q) ||
        (v.name ?? "").toLowerCase().includes(q) ||
        (v.ip ?? "").includes(q) ||
        v.last_path.toLowerCase().includes(q))
    : liste;

  const mailli = liste.filter((v) => v.email).length;

  return (
    <div className="page">
      <div className="page-head">
        <div className="page-head-row">
          <h1>Sitede Gezinenler</h1>
          <span className="muted">
            {liste.length} ziyaretçi · {mailli} tanesi maildeki bağlantıdan geldi{" "}
            <span
              className={"badge " + (canli ? "ok" : "off")}
              title={canli ? "Yeni ziyaretler anında düşer" : "Canlı bağlantı yok — Yenile'yi kullanın"}
            >
              {canli ? "canlı" : "bağlantı yok"}
            </span>
          </span>
        </div>
        <p className="page-sub">
          Siteyi gezenler. <strong>Maildeki bağlantıdan gelmeyen ziyaretçiler
          de listelenir.</strong> Ölçüm sunucu tarafında yapıldığı için reklam
          engelleyiciler bu listeyi etkilemez. Bir satıra tıklayarak o
          ziyaretçinin gezinti akışını açabilirsiniz.
        </p>
      </div>

      <div className="toolbar">
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginLeft: "auto" }}>
          <select value={dakika} onChange={(e) => setDakika(Number(e.target.value))}>
            {ARALIKLAR.map((a) => (
              <option key={a.dk} value={a.dk}>Son {a.etiket}</option>
            ))}
          </select>
          <button className="btn-primary" onClick={() => void yukle()}>Yenile</button>
        </div>
      </div>

      <div className="data-table-card">
        {/* Arama kartın İÇİNDE — filtre, filtrelediği listeyle aynı yüzeyde. */}
        <div className="data-table-toolbar">
          <label className="data-search">
            <Icon name="search" size={15} />
            <input
              type="search"
              placeholder="E-posta, ad, IP veya sayfa ara…"
              value={arama}
              onChange={(e) => setArama(e.target.value)}
              aria-label="Ziyaretçilerde ara"
            />
          </label>
          <span className="data-count">
            {arama.trim() ? `${gorunen.length}/${liste.length}` : liste.length} ziyaretçi
          </span>
        </div>
        {yukleniyor ? (
          <div className="empty-state"><div className="spinner-sm" /><p>Yükleniyor…</p></div>
        ) : gorunen.length === 0 ? (
          <div className="empty-state">
            <Icon name="globe" size={34} />
            <p>{liste.length === 0 ? "Bu aralıkta ziyaretçi yok." : "Aramaya uyan ziyaretçi yok."}</p>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Ziyaretçi</th>
                <th>Son görülme</th>
                <th>Şu anki sayfa</th>
                <th>Sayfa</th>
                <th>IP</th>
                <th>Cihaz / tarayıcı</th>
                <th>Dil</th>
                <th>Kaynak</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {gorunen.map((v) => (
                <tr
                  key={v.visitor_key}
                  onClick={() => navigate(`/ziyaretciler/${encodeURIComponent(v.visitor_key)}`)}
                  style={{ cursor: "pointer" }}
                  title="Bu ziyaretçinin gezintisini aç"
                >
                  <td>
                    {v.email ? (
                      <>
                        <div>{v.name || v.email}</div>
                        <div className="muted" style={{ fontSize: 12 }}>
                          {v.email} · maildeki bağlantıdan
                        </div>
                      </>
                    ) : (
                      <>
                        <div>Anonim ziyaretçi</div>
                        <div className="muted" style={{ fontSize: 12 }}>#{v.visitor_key.slice(0, 8)}</div>
                      </>
                    )}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <span className="badge ok">{gecenSure(v.last_seen)}</span>
                    <div className="muted" style={{ fontSize: 12 }}>{saat(v.last_seen)}</div>
                  </td>
                  <td style={{ maxWidth: 220, wordBreak: "break-all" }}>{v.last_path}</td>
                  <td>{v.page_count}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{v.ip || "—"}</td>
                  <td className="muted" style={{ fontSize: 12 }} title={ortamCoz(v.user_agent).detay}>{ortamCoz(v.user_agent).ozet}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{dilCoz(v.lang)}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{kaynakCoz(v.referer, Boolean(v.email))}</td>
                  <td className="row-actions"><button className="link">Gezinti →</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="muted" style={{ margin: "12px 0 0", fontSize: 12 }}>
        Anonim ziyaretçiler IP + tarayıcı özetiyle ayırt edilir; aynı ağdan
        aynı tarayıcıyla giren iki kişi tek ziyaretçi görünebilir. Maildeki
        bağlantıdan gelenler kesin eşleşir.
      </p>
    </div>
  );
}
